import { Theme } from '@mui/material/styles';
import { useCallback, useState } from 'react';

import { Drawer, DrawerProps } from '@/components/Layout/Drawer';
import { QuestionsContainer } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.styled';

import { useTranslate } from '@/hooks/useTranslate';

import { QuestionOtherCountriesTaxStatus } from './QuestionOtherCountriesTaxStatus/QuestionOtherCountriesTaxStatus';
import { QuestionUSTaxReturn } from './QuestionUSTaxReturn/QuestionUSTaxReturn';
import { TaxInfoDrawerInfoButton } from './TaxInfoDrawerInfoButton';

const TAX_INFO_TOTAL_QUESTIONS = 2;

type TaxInfoDrawerProps = DrawerProps & {
  startingQuestionIndex?: number;
  /** Called when both tax questions are answered (user data already refreshed). */
  onQuestionsComplete?: () => void;
};

export function TaxInfoDrawer({
  open,
  onClose,
  onQuestionsComplete,
  startingQuestionIndex = 0,
  ...props
}: TaxInfoDrawerProps) {
  const { t } = useTranslate();
  const [currentIndex, setCurrentIndex] = useState(startingQuestionIndex);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset when the drawer opens (e.g. after a previous session), without an
  // effect that re-sets the same initial value on mount.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setCurrentIndex(startingQuestionIndex);
    }
  }

  const onNext = useCallback(() => {
    setCurrentIndex(prev => {
      const nextIndex = prev + 1;

      if (nextIndex >= TAX_INFO_TOTAL_QUESTIONS) {
        if (onQuestionsComplete) {
          onQuestionsComplete();
        } else {
          onClose();
        }
        return prev;
      }

      return nextIndex;
    });
  }, [onClose, onQuestionsComplete]);

  const renderQuestion = () => {
    switch (currentIndex) {
      case 0:
        return (
          <QuestionUSTaxReturn
            index={0}
            total={TAX_INFO_TOTAL_QUESTIONS}
            onAnswered={onNext}
          />
        );
      case 1:
        return (
          <QuestionOtherCountriesTaxStatus
            index={1}
            total={TAX_INFO_TOTAL_QUESTIONS}
            onAnswered={onNext}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      {...props}
      open={open}
      onClose={onClose}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
      title={t('documentSection.Tax_Info.title')}
      rightSlot={<TaxInfoDrawerInfoButton />}
    >
      <QuestionsContainer>{renderQuestion()}</QuestionsContainer>
    </Drawer>
  );
}
